<script setup>
import SectionHeading from "@/components/settings/ui/SectionHeading.vue";
import DefaultSection from "@/components/settings/ui/DefaultSection.vue";
import SectionSubText from "@/components/settings/ui/SectionSubText.vue";
import DefaultButton from "@/components/global-themed/buttons/DefaultButton.vue";
import CustomToggle from "@/components/global-themed/CustomToggle.vue";
import PackageCard from "@/components/settings/billing/partials/PackageCard.vue";
import {useBillingPackages} from '@/composables/useBillingPackages';
import {usePausedPrice} from '@/composables/usePausedPrice';
import LoadingBlock from "@/components/global-themed/LoadingBlock.vue";
import {computed, onMounted, watch} from "vue";
import moment from "moment";
import useWebsite from "@/composables/useWebsite";
import {handle3dSecure} from "@/composables/useStripe";
import Swal from "sweetalert2";

const props = defineProps({
  portalUrl: {
    required: false,
    type: [String, null],
    default: null,
  },
  terminateAt: {
    type: [String, null],
    default: null,
  }
});

onMounted(() => terminateAtRef.value = props.terminateAt);

const { isYearly, data: packages, isLoading, terminateAt: terminateAtRef, pauseWebsiteMutation, unpauseWebsiteMutation } = useBillingPackages();
const { data: pausedPrice, isLoading: isPausedPriceLoading } = usePausedPrice();

const currentPackage = computed(() => {
  return packages.value?.find(product => product.current);
});

const getVariant = (item) => {
  if (item.deliverables.number_of_websites < currentPackage.value?.deliverables.number_of_websites) {
    return 'primary-inverted';
  }
  return 'primary';
}

const terminateAtDate = computed(() => terminateAtRef.value ? moment(terminateAtRef.value).format("Do MMM YYYY") : null);
const { website, isRefetching: isLoadingWebsite } = useWebsite();

const { mutate: pause, isPending: pausePending, error: pauseError } = pauseWebsiteMutation();
const { mutate: unpause, isPending: unpausePending, error: unpauseError } = unpauseWebsiteMutation();

watch(pauseError, () => handle3dSecure(pauseError, pause));
watch(unpauseError, () => handle3dSecure(unpauseError, unpause));

const websitePaused = computed(() => {
  if (isLoading.value || isLoadingWebsite.value) {
    return true;
  }

  return website.value?.paused === 1;
});

const handleUnpause = async () => {
  const result = await Swal.fire({
    title: 'Confirm Account Upgrade',
    html: 'Are you sure you would like to upgrade your account?',
    icon: 'question',
    showCancelButton: true,
    reverseButtons: true,
    cancelButtonText: 'No, cancel',
    confirmButtonText: 'Yes, upgrade',
  });

  if (!result.isConfirmed) {
    return;
  }

  unpause();
}

const handlePause = async () => {
  const result = await Swal.fire({
    title: 'Confirm Account Downgrade',
    html: 'Are you sure you would like to downgrade your account?',
    icon: 'question',
    showCancelButton: true,
    reverseButtons: true,
    cancelButtonText: 'No, cancel',
    confirmButtonText: 'Yes, downgrade',
  });

  if (!result.isConfirmed) {
    return;
  }

  pause();
}
</script>

<template>
  <div class="tw-flex tw-flex-col tw-gap-10">
    <div
      v-if="!terminateAtRef && !websitePaused"
      class="tw-flex tw-flex-col tw-gap-2"
    >
      <SectionHeading>
        Company Settings
      </SectionHeading>

      <DefaultSection>
        <div class="tw-flex tw-flex-col tw-gap-12">
          <div class="tw-flex tw-flex-col tw-gap-4 tw-items-center">
            <span class="tw-flex tw-gap-3 tw-text-neutral-900 tw-items-center">
              Billed Monthly
              <CustomToggle v-model="isYearly" />
              Billed Yearly
            </span>
            <span class="tw-bg-primary tw-text-white tw-rounded-lg tw-py-3 tw-px-4">
              Save 2 months with all yearly subscriptions.
            </span>
          </div>

          <LoadingBlock v-if="isLoading" />
          <div
            v-else
            class="tw-flex tw-justify-center"
          >
            <div class="tw-flex tw-gap-6 tw-overflow-x-scroll tw-items-end visible-scrollbar">
              <PackageCard
                v-for="item in packages"
                :key="item.id"
                :product="item"
                :variant="getVariant(item)"
              />
            </div>
          </div>
        </div>
      </DefaultSection>
    </div>

    <div
      v-if="portalUrl"
      class="tw-flex tw-flex-col tw-gap-2"
    >
      <SectionHeading>
        Payment and invoices
      </SectionHeading>

      <DefaultSection>
        <div class="tw-flex tw-items-center tw-justify-between">
          <SectionSubText>Manage your billing method</SectionSubText>

          <DefaultButton
            as="a"
            :href="portalUrl"
            target="_blank"
            class="!tw-py-2 !tw-px-6"
          >
            Change Payment Method
          </DefaultButton>
        </div>

        <div class="tw-flex tw-items-center tw-justify-between">
          <SectionSubText>View previous invoices</SectionSubText>

          <DefaultButton
            as="a"
            :href="portalUrl"
            target="_blank"
            class="!tw-py-2 !tw-px-6"
          >
            View Invoices
          </DefaultButton>
        </div>
      </DefaultSection>
    </div>

    <div
      v-if="currentPackage?.deliverables.number_of_websites === 1 && website"
      class="tw-flex tw-flex-col tw-gap-2"
    >
      <SectionHeading>
        {{ website?.paused ? 'Upgrade Account' : 'Downgrade Account' }}
      </SectionHeading>

      <DefaultSection v-if="isYearly">
        <SectionSubText>
          Sorry, you cannot pause whilst subscribed to an annual plan. Please contact support.
        </SectionSubText>
      </DefaultSection>

      <DefaultSection v-else-if="website?.paused">
        <div class="tw-flex tw-items-center tw-justify-between tw-gap-8">
          <SectionSubText>
            Your website is currently downgraded right now. If you want to upgrade and have full access to all data then click the 'Upgrade Account' button. You will be billed from today onwards and any pro-rata payment will be added to your next payment.
          </SectionSubText>

          <DefaultButton
            variant="danger"
            class="tw-text-nowrap"
            :disabled="unpausePending"
            @click="handleUnpause"
          >
            Upgrade Account
          </DefaultButton>
        </div>
      </DefaultSection>

      <DefaultSection v-else>
        <div class="tw-flex tw-items-center tw-justify-between tw-gap-8">
          <div class="tw-space-y-4">
            <SectionSubText>
              We know that from time to time you might need to downgrade your subscription, you can do that here. By downgrading your subscription all of your data will stay in the platform but only your keywords will continue to be checked. If you want to reaccess your data you can do by upgrading your account.
            </SectionSubText>
            <SectionSubText v-if="!isPausedPriceLoading && pausedPrice">
              <strong>You will be billed {{ pausedPrice }} per month by downgrading your website.</strong>
            </SectionSubText>
          </div>

          <DefaultButton
            variant="danger"
            class="tw-text-nowrap"
            :disabled="pausePending"
            @click="handlePause"
          >
            Downgrade Account
          </DefaultButton>
        </div>
      </DefaultSection>
    </div>

    <div class="tw-flex tw-flex-col tw-gap-2">
      <SectionHeading>
        Cancel your plan
      </SectionHeading>
      <DefaultSection v-if="!terminateAtRef">
        <div class="tw-flex tw-items-center tw-justify-between">
          <SectionSubText>If you wish to cancel your account please click the cancel subscription button</SectionSubText>

          <DefaultButton
            variant="danger"
            as="a"
            href="/settings/package/cancel"
          >
            Cancel Subscription
          </DefaultButton>
        </div>
      </DefaultSection>

      <DefaultSection v-else>
        <div class="tw-flex tw-flex-col tw-gap-2">
          <SectionSubText>Your plan has been cancelled.</SectionSubText>
          <SectionSubText>You will continue to have access until the end of your billing period: {{ terminateAtDate }}</SectionSubText>
          <SectionSubText>If you would like to stop this cancellation, please reach out to our support team</SectionSubText>
        </div>
      </DefaultSection>
    </div>
  </div>
</template>
